import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { MOCK_ROUTES } from 'src/app/components/reports/reports.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, mergeMap, take } from 'rxjs';
import { UserAdmin } from 'src/app/core/models/admin';
import { LoadDiscrepancy } from 'src/app/core/models/reports/reports.model';
import { OnRoadOperationsService } from 'src/app/core/services/reports/on-road-operations/on-road-operations.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  LoadDiscrepenancyReportFormGroup,
  PDF_REPORT,
  REPORT_FILE_NAME,
} from './load-discrepenancy-reports.constants';

@Component({
  selector: 'app-load-discepenancy-report',
  templateUrl: './load-discepenancy-report.component.html',
  styleUrls: ['./load-discepenancy-report.component.scss'],
  standalone: false,
})
export class LoadDiscrepenancyReportComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: LoadDiscrepenancyReportFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<LoadDiscrepenancyReportComponent> = inject(
    MatDialogRef<LoadDiscrepenancyReportComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private onRoadOperationService: OnRoadOperationsService = inject(
    OnRoadOperationsService,
  );
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  loadDiscrepenancyReportForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  routes = MOCK_ROUTES;
  currentUser!: UserAdmin | undefined;
  locationCode = '';
  groupCode = '';
  loop = '';
  reportDate = '';
  loadDiscrepancyReport!: LoadDiscrepancy[];

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit() {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        mergeMap((user) => {
          this.locationCode = user?.location;
          this.currDate = new Date();
          this.minDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() - 30,
          );
          this.maxDate = new Date(
            this.currDate.getFullYear(),
            this.currDate.getMonth(),
            this.currDate.getDate() + 14,
          );

          this.loadDiscrepenancyReportForm = this.fb.group({
            fromDate: [this.currDate],
            toDate: [this.currDate],
            routes: [this.routes[0]],
            reportFormat: [Options.PDF],
          });

          return this.getRouteList();
        }),
      )
      .subscribe();
  }

  clickExit() {
    this.commonUtils.openExitModal(this.loadDiscrepenancyReportForm.dirty);
  }

  radioChange(newValue: Options) {
    this.loadDiscrepenancyReportForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof LoadDiscrepenancyReportComponent
   */
  getReport() {
    const formValue = this.loadDiscrepenancyReportForm.value;

    const fromDate = this.commonUtils.getFormattedDate(formValue.fromDate);
    const toDate = this.commonUtils.getFormattedDate(formValue.toDate);

    const request = {
      locationCode: this.locationCode,
      fromDate,
      toDate,
      loop: formValue.routes?.value === 'All' ? 'All' : formValue.routes,
    };
    this.onRoadOperationService
      .getLoadDiscrepancyReport(request)
      .pipe(take(1))
      .subscribe((data) => {
        const formatType =
          this.loadDiscrepenancyReportForm.get('reportFormat')?.value;
        const loadDiscrepancy = data.response.loadDiscrepancy;

        if (loadDiscrepancy?.length > 0) {
          if (formatType === Options.CSV) {
            const customHeader = [
              ['', '', 'LOAD DISCREPANCY REPORT', '', '', ''],
              [
                'Group',
                `${data.response.groupCode}`,
                '',
                'Loop',
                `${data.response.loop}`,
                '',
                'Report Date',
                `${data.response.reportDate}`,
              ],
            ];
            const formatedDate = formatDate(new Date(), 'MM-dd-yyyy', 'en-US');
            const customFooter = [[], ['Printed:', `${formatedDate}`]];
            this.reportUtils.exportCSVReport(
              loadDiscrepancy,
              REPORT_FILE_NAME,
              customHeader,
              customFooter,
            );
          }
          if (formatType === Options.PDF) {
            this.groupCode = data.response.groupCode;
            this.loop = data.response.loop;
            this.reportDate = data.response.reportDate;

            this.loadDiscrepancyReport = loadDiscrepancy;
            setTimeout(() => {
              const content = this.pdfContainer.element.nativeElement.innerHTML;
              this.reportUtils.exportPdfReport(
                content,
                PDF_REPORT.NAME,
                REPORT_FILE_NAME,
              );
            }, 0);
          }
        } else {
          this.commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
        }
        this.dialogRef.close();
      });
  }

  /**
   * Used to get route list
   *
   * @private
   * @memberof LoadDiscrepenancyReportComponent
   */
  private getRouteList() {
    return this.onRoadOperationService
      .getLoadDiscrepancyRoutes(this.locationCode)
      .pipe(
        take(1),
        map((data) => {
          this.routes = data.response.loadDiscrepancyRoutes
            .map(({ route }) => {
              return {
                label: route,
                value: route,
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label));
          this.routes.unshift({
            label: $localize`All`,
            value: 'All',
          });
        }),
      );
  }
}
